"use client";
import { Trash2, Clock, User, Phone, Calendar, Search, X, List, RotateCcw, FileSpreadsheet, ShieldCheck, Users as UsersIcon, Columns3 } from "lucide-react";
import { useState, useEffect } from "react";

import { formatPhoneNumber } from "@/app/_module/utils/formatters";

import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/app/_module/components/ui/dialog";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Separator } from "@/app/_module/components/ui/separator";
import { DataPagination, type PaginationMeta } from "@/app/_module/components/ui/data-pagination";

import { useDeletedLeads, usePermanentlyDeleteLead, usePermanentlyDeleteAllLeads, useRestoreDeletedLead, useRestoreAllDeletedLeads, useDeletedPipelineStages, useRestorePipelineStage } from "../hooks/queries";
import { useGetDeletedImports, useRestoreImport } from "../hooks/leads-import-queries";
import type{ DeletedLead } from "../types/get-deleted-leads-type";

interface RecentlyDeletedDialogProps {
  children: React.ReactNode;
}

export const RecentlyDeletedDialog = function ({ children }: RecentlyDeletedDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<"leads" | "lists" | "pipelines">("leads");
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showRestoreAllConfirm, setShowRestoreAllConfirm] = useState(false);

  // Deleted whole lists (uploads). Loaded lazily when the Lists tab is opened.
  const { data: deletedImportsResp, isLoading: importsLoading } = useGetDeletedImports({ enabled: isOpen && tab === "lists" });
  const { mutate: restoreImport, isPending: isRestoringImport, variables: restoringImportId } = useRestoreImport();

  // Deleted pipeline stages. Loaded lazily when the Pipelines tab is opened.
  // Born from a real ticket: an owner deleted a stage holding 275 leads and the
  // only road back was a support DM — now it's a Restore button.
  const { data: deletedStagesResp, isLoading: stagesLoading } = useDeletedPipelineStages({ enabled: isOpen && tab === "pipelines" });
  const { mutate: restoreStage, isPending: isRestoringStage, variables: restoringStageId } = useRestorePipelineStage();
  const deletedStages: any[] = Array.isArray(deletedStagesResp)
    ? deletedStagesResp
    : ((deletedStagesResp as any)?.data || []);
  const deletedImports: any[] = Array.isArray(deletedImportsResp)
    ? deletedImportsResp
    : ((deletedImportsResp as any)?.items || (deletedImportsResp as any)?.data?.items || (deletedImportsResp as any)?.data || []);
  const [listSearch, setListSearch] = useState("");
  const filteredDeletedImports = deletedImports.filter((imp: any) => {
    const q = listSearch.trim().toLowerCase();
    return !q || (imp.fileName || imp.name || "").toLowerCase().includes(q);
  });

  // Search state — `searchInput` is what the user is typing, `searchQuery`
  // is the debounced value sent to the server (300ms after last keystroke
  // so we don't hammer the API on every character).
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Pagination state — page-size lives separately so the user can keep
  // 100 per page even after searching/clearing the filter.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Reset to page 1 whenever the search query changes — otherwise typing a
  // new query while on page 3 could land you on a page that no longer
  // exists for the smaller result set.
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const { data: deletedLeadsResponse, isLoading, error, refetch } =
    useDeletedLeads(searchQuery, page, pageSize);

  // Auto-refresh when dialog opens
  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  // Reset search + pagination when the dialog closes so reopening starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setSearchInput("");
      setSearchQuery("");
      setPage(1);
      setTab("leads");
      setListSearch("");
    }
  }, [isOpen]);
  const { mutateAsync: restoreDeletedLead, isPending: isRestoring } = useRestoreDeletedLead();
  const { mutateAsync: restoreAllDeletedLeads, isPending: isRestoringAll } = useRestoreAllDeletedLeads();
  const { mutateAsync: permanentlyDeleteLead, isPending: isPermanentlyDeleting } = usePermanentlyDeleteLead();
  const { mutateAsync: permanentlyDeleteAllLeads, isPending: isPermanentlyDeletingAll } = usePermanentlyDeleteAllLeads();

  // Debug logging
  // Fix: Handle different response structures from handleApiCall
  // handleApiCall extracts the data field, so deletedLeadsResponse is already the extracted data
  const deletedLeads =
    deletedLeadsResponse?.items ||
    (Array.isArray(deletedLeadsResponse?.data) ? deletedLeadsResponse.data : deletedLeadsResponse?.data?.items) ||
    (Array.isArray(deletedLeadsResponse) ? deletedLeadsResponse : []);
  const hasDeletedLeads = deletedLeads.length > 0;

  // Pull total count from the paginated meta — the API returns up to 50 per
  // page, so deletedLeads.length is just the items on the current page, not
  // the true total. Without this, a user with 500 deleted leads sees "50".
  const rawMeta =
    (deletedLeadsResponse as any)?.meta ||
    (deletedLeadsResponse as any)?.data?.meta;
  const totalDeleted: number =
    rawMeta?.totalItems ?? deletedLeads.length;

  // Adapt backend's paginate-service meta shape to the DataPagination
  // component's expected PaginationMeta shape. Backend gives us page,
  // nextPage, prevPage, totalItems, totalPages, itemCount, offset; the
  // component wants page, limit, totalPages, totalItems, itemCount,
  // hasNextPage, hasPreviousPage.
  const paginationMeta: PaginationMeta | null = rawMeta?.totalItems != null
    ? {
        page: rawMeta.page ?? page,
        limit: rawMeta.offset ?? pageSize,
        totalItems: rawMeta.totalItems ?? 0,
        totalPages: rawMeta.totalPages ?? 1,
        itemCount: rawMeta.itemCount ?? deletedLeads.length,
        hasNextPage: rawMeta.nextPage != null,
        hasPreviousPage: rawMeta.prevPage != null,
      }
    : null;

  const formatDate = (dateString: string) => {
    // Parse date safely - handle both ISO datetime and YYYY-MM-DD formats
    let date: Date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      // Parse YYYY-MM-DD as local date to avoid UTC shift
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day, 12, 0, 0, 0);
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Deleted leads are permanently purged after this many days (backend cron
  // `purge-deleted-leads`, LEAD_TRASH_RETENTION_DAYS, default 60). Keep in sync.
  const RETENTION_DAYS = 60;

  // Days left before a deleted lead is permanently removed (0 = imminent).
  const getDaysLeft = (dateString: string) => {
    let deletedDate: Date;
    if (dateString.includes('T')) {
      deletedDate = new Date(dateString);
    } else {
      const [year, month, day] = dateString.split('-').map(Number);
      deletedDate = new Date(year, month - 1, day, 12, 0, 0, 0);
    }
    const elapsedDays = Math.floor((Date.now() - deletedDate.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, RETENTION_DAYS - elapsedDays);
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    // Parse date safely - handle both ISO datetime and YYYY-MM-DD formats
    let deletedDate: Date;
    if (dateString.includes('T')) {
      deletedDate = new Date(dateString);
    } else {
      const [year, month, day] = dateString.split('-').map(Number);
      deletedDate = new Date(year, month - 1, day, 12, 0, 0, 0);
    }
    const diffInMs = now.getTime() - deletedDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return `${diffInDays}d ago`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new lead":
        return "bg-blue-100 text-blue-800";
      case "appt":
      case "appointment":
      case "scheduled":
        return "bg-amber-500 text-amber-100";
      case "underwriting":
      case "under writing":
        return "bg-cyan-600 text-cyan-100";
      case "sold & approved":
      case "approved":
        return "bg-emerald-600 text-emerald-100";
      case "bad number":
      case "bad #":
        return "bg-red-500 text-red-100";
      case "no sale":
        return "bg-red-600 text-red-100";
      case "do not call":
        return "bg-amber-800 text-amber-100";
      case "disposed":
        return "bg-gray-600 text-gray-100";
      default:
        return "bg-gray-600 text-gray-100";
    }
  };

  const handleRestoreLead = async (leadId: string) => {
    try {
      await restoreDeletedLead(leadId);
      // Don't immediately close - let user see the result
      setTimeout(() => {
        if (deletedLeads.length <= 1) {
          setIsOpen(false);
        }
      }, 1000);
    } catch (error) {

      // Keep dialog open for retry
    }
  };

  const handlePermanentlyDeleteLead = async (leadId: string) => {
    try {
      await permanentlyDeleteLead(leadId);
      // Don't immediately close - let user see the result
      setTimeout(() => {
        if (deletedLeads.length <= 1) {
          setIsOpen(false);
        }
      }, 1000);
    } catch (error) {

      // Keep dialog open for retry
    }
  };

  const handlePermanentlyDeleteAll = async () => {
    try {
      await permanentlyDeleteAllLeads();
      setShowDeleteAllConfirm(false);
      // Close the dialog after a short delay
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    } catch (error) {
      // Keep dialog open for retry
      setShowDeleteAllConfirm(false);
    }
  };

  const handleRestoreAll = async () => {
    try {
      await restoreAllDeletedLeads();
      setShowRestoreAllConfirm(false);
      // Close the dialog after a short delay
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    } catch (error) {
      // Keep dialog open for retry
      setShowRestoreAllConfirm(false);
    }
  };

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <ResponsiveDialogContent size="3xl" className="m-deleted-modal h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            Recently Deleted Leads
            {hasDeletedLeads && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                {totalDeleted.toLocaleString()}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {searchQuery
              ? hasDeletedLeads
                ? `${totalDeleted.toLocaleString()} match${totalDeleted === 1 ? '' : 'es'} for "${searchQuery}"`
                : `No matches for "${searchQuery}"`
              : hasDeletedLeads
                ? `${totalDeleted.toLocaleString()} deleted lead${totalDeleted === 1 ? '' : 's'} · kept ${RETENTION_DAYS} days, then permanently removed`
                : `Deleted leads are restorable for ${RETENTION_DAYS} days, then permanently removed`}
          </DialogDescription>
        </DialogHeader>

        {/* Tab toggle: individual deleted leads vs whole deleted lists */}
        <div className="flex-shrink-0 pt-3 flex items-center gap-1 border-b">
          <button
            type="button"
            onClick={() => setTab("leads")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "leads" ? "border-red-500 text-red-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <span className="inline-flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Deleted Leads</span>
          </button>
          <button
            type="button"
            onClick={() => setTab("lists")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "lists" ? "border-red-500 text-red-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <span className="inline-flex items-center gap-1.5"><List className="h-4 w-4" /> Deleted Lists</span>
          </button>
          <button
            type="button"
            onClick={() => setTab("pipelines")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "pipelines" ? "border-red-500 text-red-600" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <span className="inline-flex items-center gap-1.5"><Columns3 className="h-4 w-4" /> Deleted Pipelines</span>
          </button>
        </div>

        {/* Search input (leads tab only) */}
        {tab === "leads" && (
        <div className="flex-shrink-0 pt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, phone, or email…"
              className="w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        )}

        {/* Action buttons - right below header. Hidden when there's nothing
            to act on (no deleted leads → no Restore/Delete buttons to show). */}
        {tab === "leads" && (
        <div
          className={`flex-shrink-0 flex items-center justify-between gap-4 ${
            hasDeletedLeads ? 'py-4 border-b' : ''
          }`}
        >
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            {/* Restore All Button */}
            {hasDeletedLeads && !showRestoreAllConfirm && !showDeleteAllConfirm && (
              <Button
                disabled={isRestoringAll}
                onClick={() => setShowRestoreAllConfirm(true)}
                variant="default"
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                {isRestoringAll ? "Restoring All..." : "Restore All"}
              </Button>
            )}
            {showRestoreAllConfirm && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-green-600 font-medium">
                  Restore all deleted leads?
                </span>
                <Button
                  disabled={isRestoringAll}
                  onClick={handleRestoreAll}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isRestoringAll ? "Restoring..." : "Yes, Restore All"}
                </Button>
                <Button
                  disabled={isRestoringAll}
                  onClick={() => setShowRestoreAllConfirm(false)}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Delete All Button */}
            {hasDeletedLeads && !showDeleteAllConfirm && !showRestoreAllConfirm && (
              <Button
                disabled={isPermanentlyDeletingAll}
                onClick={() => setShowDeleteAllConfirm(true)}
                variant="destructive"
                size="sm"
              >
                {isPermanentlyDeletingAll ? "Deleting All..." : "Permanently Delete All"}
              </Button>
            )}
            {showDeleteAllConfirm && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-red-600 font-medium">
                  Are you sure? This cannot be undone.
                </span>
                <Button
                  disabled={isPermanentlyDeletingAll}
                  onClick={handlePermanentlyDeleteAll}
                  size="sm"
                  variant="destructive"
                >
                  {isPermanentlyDeletingAll ? "Deleting..." : "Yes, Delete All"}
                </Button>
                <Button
                  disabled={isPermanentlyDeletingAll}
                  onClick={() => setShowDeleteAllConfirm(false)}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
        )}

        <div className="flex-1 min-h-0 mt-4 overflow-hidden">
          {tab === "pipelines" ? (
            stagesLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span>Loading deleted pipelines...</span>
              </div>
            ) : deletedStages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Columns3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No deleted pipelines</p>
                <p className="text-sm">Custom pipeline stages you delete show up here to restore.</p>
              </div>
            ) : (
              <ScrollArea className="h-full pr-4">
                <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md p-2 mb-3">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
                  <span>Restoring a pipeline brings the column back <strong>with its leads</strong>. Leads you&rsquo;ve worked since the delete (sold, booked, reassigned) stay where they are.</span>
                </div>
                <div className="space-y-2">
                  {deletedStages.map((st: any) => {
                    const busy = isRestoringStage && restoringStageId === st.id;
                    return (
                      <div key={st.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-3 w-3 rounded-full shrink-0 border border-black/10"
                              style={{ backgroundColor: st.color || "#6b7280" }}
                              aria-hidden="true"
                            />
                            <span className="font-medium text-gray-900 truncate">{st.name}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                            <span className="tabular-nums">
                              {Number(st.leadsInStage || 0).toLocaleString()} lead{st.leadsInStage === 1 ? "" : "s"} will come back
                            </span>
                            {st.deletedAt && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> deleted {getTimeAgo(st.deletedAt)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => restoreStage(st.id)}
                          className="bg-green-600 hover:bg-green-700 shrink-0"
                        >
                          <RotateCcw className="h-4 w-4 mr-1.5" />
                          {busy ? "Restoring..." : "Restore pipeline"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )
          ) : tab === "lists" ? (
            importsLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span>Loading deleted lists...</span>
              </div>
            ) : deletedImports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <List className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No deleted lists</p>
                <p className="text-sm">Whole CSV lists you delete show up here to restore.</p>
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="flex-shrink-0 relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Search deleted lists by file name…"
                    className="w-full rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  {listSearch && (
                    <button type="button" onClick={() => setListSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Clear search">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <ScrollArea className="flex-1 min-h-0 pr-4">
                <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md p-2 mb-3">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
                  <span>Restoring a list brings back every lead it removed. Appointments, sold, approved &amp; underwriting leads were never deleted — they&rsquo;re always kept.</span>
                </div>
                {filteredDeletedImports.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">No lists match &quot;{listSearch}&quot;</div>
                ) : (
                <div className="space-y-2">
                  {filteredDeletedImports.map((imp: any) => {
                    const counts = imp.resultCounts || {};
                    const created = counts.created ?? 0;
                    const fileName = imp.fileName || imp.name || "Untitled import";
                    const busy = isRestoringImport && restoringImportId === imp.id;
                    return (
                      <div key={imp.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileSpreadsheet className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="font-medium text-gray-900 truncate">{fileName}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                            <span className="tabular-nums">{created.toLocaleString()} lead{created === 1 ? "" : "s"} to restore</span>
                            {imp.deletedAt && (<><span className="text-gray-300">·</span><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> deleted {getTimeAgo(imp.deletedAt)}</span></>)}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => restoreImport(imp.id)}
                          className="bg-green-600 hover:bg-green-700 shrink-0"
                        >
                          <RotateCcw className="h-4 w-4 mr-1.5" />
                          {busy ? "Restoring..." : "Restore list"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
                )}
                </ScrollArea>
              </div>
            )
          ) : isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span>Loading deleted leads...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 mb-2">
                <Trash2 className="h-12 w-12 mx-auto opacity-50" />
              </div>
              <p className="text-sm text-muted-foreground">
                Failed to load deleted leads
              </p>
            </div>
          ) : !hasDeletedLeads ? (
            <div className="text-center py-8">
              <div className="text-muted-foreground">
                {searchQuery ? (
                  <>
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No matches</p>
                    <p className="text-sm">
                      No deleted leads match &quot;{searchQuery}&quot;
                    </p>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No deleted leads</p>
                    <p className="text-sm">
                      No leads have been deleted recently
                    </p>
                  </>
                )}
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full pr-4">
              <div className="space-y-4">
                {Array.isArray(deletedLeads) && deletedLeads.map((lead: DeletedLead) => (
                  <div
                    key={lead.id}
                    className="border border-gray-200 rounded-lg p-4 space-y-3"
                  >
                    {/* Lead Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getStatusColor(lead.leadStat)}>
                            {lead.leadStat || "Unknown"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Deleted {getTimeAgo(lead.deletedAt)}
                          </span>
                          {(() => {
                            const daysLeft = getDaysLeft(lead.deletedAt);
                            const urgent = daysLeft <= 7;
                            return (
                              <span
                                className={`text-xs font-medium ${urgent ? "text-red-600" : "text-amber-600"}`}
                                title={`Permanently removed ${daysLeft} day${daysLeft === 1 ? "" : "s"} from now (kept ${RETENTION_DAYS} days)`}
                              >
                                · {daysLeft}d left
                              </span>
                            );
                          })()}
                        </div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {lead.name || "Unnamed Lead"}
                        </h4>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div className="flex items-center gap-1 mb-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(lead.deletedAt)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {!!lead.createdAt && formatDate(lead.createdAt.toString())}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Lead Details */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-4 w-4" />
                          <span>Phone</span>
                        </div>
                        <p className="font-medium text-gray-900">
                          {lead.phone ? formatPhoneNumber(lead.phone) : "Not provided"}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="text-gray-600">Email</div>
                        <p className="font-medium text-gray-900">
                          {lead.email || "Not provided"}
                        </p>
                      </div>

                      {!!lead.age && (
                        <div className="space-y-2">
                          <div className="text-gray-600">Age</div>
                          <p className="font-medium text-gray-900">{lead.age}</p>
                        </div>
                      )}

                      {!!lead.assigned && (
                        <div className="space-y-2">
                          <div className="text-gray-600">Assigned To</div>
                          <p className="font-medium text-gray-900">{lead.assigned}</p>
                        </div>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Lead ID: {lead.id}</span>
                        <span>Created: {!!lead.createdAt && formatDate(lead.createdAt.toString())}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <Button
                        disabled={isRestoring}
                        onClick={() => handleRestoreLead(lead.id)}
                        variant="outline"
                      >
                        {isRestoring ? "Restoring..." : "Restore"}
                      </Button>

                      <Button
                        disabled={isPermanentlyDeleting}
                        onClick={() => handlePermanentlyDeleteLead(lead.id)}
                        variant="destructive"
                      >
                        {isPermanentlyDeleting ? "Permanently Deleting..." : "Permanently Delete"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Pagination footer */}
        {tab === "leads" && paginationMeta && paginationMeta.totalPages > 1 && (
          <div className="flex-shrink-0">
            <DataPagination
              meta={paginationMeta}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[25, 50, 100, 200]}
              isLoading={isLoading}
              showPageSize={true}
            />
          </div>
        )}
      </ResponsiveDialogContent>
    </Dialog>
  );
};
